<template>
    <v-row no-gutters>
        <NoteDialog
            v-if="noteDialog.show"
            :note="noteDialog.note"
            :show-dialog="noteDialog.show"
            :forcedActiveTabType="forcedNoteActiveTabType"
            @close="closeNoteDialog"
        ></NoteDialog>

        <v-expansion-panels
            v-if="panels.length"
            :value="openPanels"
            accordion
            multiple
        >
            <v-expansion-panel
                v-for="(panel, index) in panels"
                :key="index"
                class="elevation-4"
                @change="updatePanelState(panel.groupName, newPanelStateValue(index))"
            >
                <div>
                    <SidebarPanelHeader
                        :panel="panel"
                        @addNew="addNew($event, panel)"
                    />
                    <v-expansion-panel-content v-if="relatedObject" eager>
                        <SidebarPanelContent
                            :panelFields="panel.fields"
                            :related-object="relatedObject"
                            :related-type="relatedType"
                            :sub-type="subType"
                            :group-key="panel.groupKey"
                            @viewAllNotes="viewAllNotes()"
                            @softPanelStateChange="updatePanelSoftState(panel.groupName, $event)"
                        />
                    </v-expansion-panel-content>
                </div>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-row>
</template>

<script>
import {ref, watch, computed} from "@vue/composition-api";
import ClientUsersList from "../../views/Client/ClientUsersList.vue";
import AppModule from "@/store/modules/app";
import UserModule from "@/store/modules/user";
import ClientModule from "../../store/modules/client";
import NoteModule from "../../store/modules/note";
import errorDialog from "../../views/ErrorDialog/errorDialog";
import SidebarPanelContent from "./SidebarPanelContent.vue";
import {getSidebarGroupedCustomFields, relatedTypeEnum} from "./sidebar";
import ProjectModule from "../../store/modules/project";
import TitlePanel from "./TitlePanel.vue";
import NoteDialog from "../../views/Note/NoteDialog.vue";
import router from "../../router";
import SidebarPanelHeader from "./SidebarPanelHeader.vue";

export default {
    name: "SidebarContent",
    components: {NoteDialog, TitlePanel, SidebarPanelContent, ClientUsersList, SidebarPanelHeader},
    props: {
        relatedType: {
            type: String,
            required: true,
        },
        subType: {
            type: String,
            default: null,
        }
    },
    setup(props, ctx) {
        const clientModule = ClientModule()
        const projectModule = ProjectModule()
        const noteModule = NoteModule()
        const relatedObject = computed(() => {
            if (props.relatedType === relatedTypeEnum.CLIENT) {
                return clientModule.current_client.value
            } else if (props.relatedType === relatedTypeEnum.PROJECT) {
                return projectModule.current_project.value
            }
        })
        const userModule = UserModule();
        const panels = ref([]);
        const {fetchClassifiers} = AppModule();
        const noteDialog = ref({show: false, note: null});
        const openPanels = ref([])
        const forcedNoteActiveTabType = ref("info")
        const softClosedGroupNames = ref([]) // only visually closed

        const navigateTo = (name) => {
            router.push({
                name: name,
                params: {id: relatedObject.value.id},
            });
        }
        const navigateToEdit = () => {
            if (props.relatedType === relatedTypeEnum.CLIENT) {
                clientModule.navigateToClientEdit()
            } else if (props.relatedType === relatedTypeEnum.PROJECT) {
                navigateTo("project_edit")
            }
        }
        const addNew = (addButtonType, panel) => {
            if (addButtonType === "notes") {
                forcedNoteActiveTabType.value = "info"
                onAddNoteClick()
            } else if (addButtonType === "billing_notes") {
                forcedNoteActiveTabType.value = "billing"
                onAddNoteClick()
            } else if (addButtonType === "documents") {
                const field = panel.fields.find((field) => field.field.type === 'cloud_folder')
                if (!field?.value) return
                window.open(field.value, '_blank')
            } else {
                navigateToEdit()
            }
        }
        const viewAllNotes = () => {
            if (props.relatedType === "client")
                navigateTo("client_profile_overview")
            else if (props.relatedType === "project")
                navigateTo("project_notes")
        }

        const onAddNoteClick = () => {
            noteDialog.value.note = {
                related_type: props.relatedType,
                related_id: relatedObject.value.id,
            };
            noteDialog.value.show = true;
        };
        const closeNoteDialog = () => {
            noteDialog.value.show = false;
            noteDialog.value.note = null;
        };

        const updatePanelSoftState = (groupName, state) => {
            if (state) {
                softClosedGroupNames.value = softClosedGroupNames.value.filter(name => name !== groupName)
            } else {
                softClosedGroupNames.value.push(groupName)
            }
            setOpenPanels()
        }

        const forcePanelStatusChange = (groupKey, status) => {
            const panel = panels.value.find(panel => panel.groupKey === groupKey)
            updatePanelState(panel.groupName, status)
            setOpenPanels()
        }

        const loadData = async () => {
            const accountId = relatedObject.value.account_id
            fetchClassifiers({
                types: ["custom_fields", "roles", "languages", "taxes"],
                account_id: accountId
            }).then(async () => {
                await noteModule.fetchNotes(props.relatedType, relatedObject.value.id)
                await userModule.loadMembers()
                await clientModule.fetchVisibleClients()

                if (props.relatedType === relatedTypeEnum.PROJECT) {
                    projectModule.fetchProject(relatedObject.value.id)
                }

                // In client profile overview, if sidebar hasn't fully loaded
                // and you click edit client, then it will show an error
                // this is here to fix that
                if (!relatedObject.value) {
                    return
                }

                panels.value = getSidebarGroupedCustomFields(
                    accountId,
                    props.relatedType,
                    relatedObject.value,
                    props.subType
                );
                setInitialPanelStates()
                if (props.subType === 'invoice') {
                    forcePanelStatusChange("notes", false)
                    forcePanelStatusChange("billing_notes", true)
                }
            }).catch(ex => errorDialog.showError(ex));
        };

        const getPanelStates = () => {
            let expansionPanelState = null;
            try {
                expansionPanelState = JSON.parse(localStorage.getItem('sidebar-expansion-panel-state'));
            } catch (e) {
                // pass
            }
            return expansionPanelState
        }

        const setPanelStates = (expansionPanelState) => {
            localStorage.setItem('sidebar-expansion-panel-state', JSON.stringify(expansionPanelState));
        }

        const updatePanelState = (groupName, value) => {
            let expansionPanelState = getPanelStates() || {};

            expansionPanelState[props.relatedType] = expansionPanelState[props.relatedType] || {};
            expansionPanelState[props.relatedType][groupName] = value;
            setPanelStates(expansionPanelState)
        };

        const setOpenPanels = () => {
            const expansionPanelState = getPanelStates()

            if (!expansionPanelState || !expansionPanelState[props.relatedType]) {
                return
            }
            const values = [];
            panels.value.forEach((panel, index) => {
                const groupName = panel.groupName
                const isSoftClosed = softClosedGroupNames.value.includes(groupName)

                const value = expansionPanelState[props.relatedType][groupName];
                if (value && !isSoftClosed) {
                    values.push(index);
                }
            });
            openPanels.value = values
        };

        const setInitialPanelStates = () => {
            let expansionPanelState = getPanelStates()
            if (!expansionPanelState) {
                expansionPanelState = {}
                expansionPanelState[props.relatedType] = {}
                panels.value.forEach((panel, index) => {
                    if (index < 4) {
                        expansionPanelState[props.relatedType][panel.groupName] = true
                    }
                })
                setPanelStates(expansionPanelState)
            }
            setOpenPanels()
        }

        const newPanelStateValue = (index) => {
            return !openPanels.value.includes(index)
        }

        watch(() => relatedObject.value, (newVal, oldVal) => {
            if (newVal && newVal !== oldVal) {
                ctx.emit("relatedObject", relatedObject.value)
                loadData()
            }
        })

        return {
            navigateToEdit,
            updatePanelState,
            onAddNoteClick,
            closeNoteDialog,
            addNew,
            noteDialog,
            panels,
            relatedObject,
            openPanels,
            viewAllNotes,
            updatePanelSoftState,
            forcedNoteActiveTabType,
            newPanelStateValue
        };
    }
}

</script>

<style scoped lang="scss">
.v-expansion-panel-header {
    padding-top: 9px;
    padding-bottom: 9px;
    height: 48px;
}

.v-expansion-panel {
    border-radius: 8px;
    margin-top: 1px;
}

.v-expansion-panel::after {
    display: none;
}

.v-expansion-panel--active {
    margin-top: 1px;
}

.v-expansion-panel::before {
    display: none;
}

.fixed-panel {
    position: sticky;
    top: 0;
    z-index: 5;
}

.scrollable-panel {
    flex-grow: 1;
    overflow-y: auto;
    position: relative;
}


</style>